import {
  type PointerEvent,
  type ReactNode,
  useContext,
  useId,
  useState,
  useSyncExternalStore,
} from "react";

import {
  Box,
  MotionBox,
  motionDurations,
  motionEasings,
} from "@hightouch/htui";
import { keyframes } from "@emotion/react";
import { AnimatePresence, LayoutGroup, MotionConfig } from "motion/react";

import { CompactNavContext } from "./compact-nav-context";
import { navItemBg, productNavItemBg } from "./nav-item-styles";
import {
  NavHoverContext,
  NavHoverGroupContext,
  type NavPillPlace,
  NavHoverStore,
} from "./nav-hover-store";

// Motion's own opacity animations on a shared-layout pill blink for a frame as they end, so Motion only moves and crossfades it, and CSS fades it in and out.
const PILL_TRANSITION = {
  layout: { duration: motionDurations.fast, ease: motionEasings.easeOutQuad },
  opacity: {
    duration: motionDurations.faster,
    ease: motionEasings.easeOutQuad,
  },
};
const PILL_EASE = `cubic-bezier(${motionEasings.easeOutQuad.join(", ")})`;
const PILL_FADE_IN = `${motionDurations.faster}s ${PILL_EASE}`;
const PILL_FADE_OUT = `${motionDurations.fastest}s ${PILL_EASE}`;
const pillIn = keyframes`from { opacity: 0; }`;

/** One hover pill that slides between the rows inside it, the outgoing pill fading as it follows. */
export const NavHoverProvider = ({ children }: { children: ReactNode }) => {
  const [store] = useState(() => new NavHoverStore());
  // Its own group, so the docked list and a peek's never trade pills.
  const groupId = useId();
  return (
    <NavHoverContext.Provider value={store}>
      {/* Every list's pill, not only the product nav's, holds still under reduced motion. */}
      <MotionConfig reducedMotion="user">
        <LayoutGroup id={groupId}>
          <Box
            display="contents"
            onPointerLeave={(event: PointerEvent) => {
              if (event.pointerType === "mouse") store.leave();
            }}
          >
            {children}
          </Box>
        </LayoutGroup>
      </MotionConfig>
    </NavHoverContext.Provider>
  );
};

type NavHoverRowProps = {
  /** The row's text color while the pill sits on it. */
  hoverColor: string;
  /** Marks the section's rail under the pill, for a row nested under a section. */
  hasRailTick?: boolean;
  children: ReactNode;
};

/** A row the pill can sit on. The pointer lands it here, and a gap between rows leaves it on the last one. */
export const NavHoverRow = ({
  hoverColor,
  hasRailTick,
  children,
}: NavHoverRowProps) => {
  const store = useContext(NavHoverContext);
  const group = useContext(NavHoverGroupContext);
  const rowId = useId();
  const place = useSyncExternalStore(
    store?.subscribe ?? noopSubscribe,
    () => store?.placeFor(rowId) ?? null,
  );
  if (!store) return <>{children}</>;

  return (
    <Box
      position="relative"
      data-nav-hovered={(place && !place.isOut) || undefined}
      // A touch's tap is a press, not a hover, so only a mouse moves the pill.
      onPointerEnter={(event: PointerEvent) => {
        if (event.pointerType === "mouse") store.enter(rowId, group);
      }}
      sx={{ "&[data-nav-hovered] .group": { color: hoverColor } }}
    >
      <AnimatePresence initial={false}>
        {place && (
          <NavHoverPill
            key={place.session}
            place={place}
            hasRailTick={hasRailTick}
          />
        )}
      </AnimatePresence>
      {children}
    </Box>
  );
};

const NavHoverPill = ({
  place,
  hasRailTick,
}: {
  place: NavPillPlace;
  hasRailTick?: boolean;
}) => {
  const isCompactNav = useContext(CompactNavContext);
  return (
    <MotionBox
      layoutId={`nav-hover-pill-${place.session}`}
      aria-hidden="true"
      position="absolute"
      inset={0}
      pointerEvents="none"
      exit={{ opacity: 0 }}
      transition={PILL_TRANSITION}
    >
      <Box
        position="absolute"
        inset={0}
        opacity={place.isOut ? 0 : 1}
        transition={`opacity ${PILL_FADE_OUT}`}
        animation={place.isEntering ? `${pillIn} ${PILL_FADE_IN}` : undefined}
        _mediaReduceMotion={{ animation: "none" }}
      >
        <Box
          position="absolute"
          inset={0}
          borderRadius="lg"
          // The product nav's hover is a flat, darker fill, and the classic nav's a raised, translucent one.
          {...(isCompactNav
            ? { bg: productNavItemBg.hover }
            : {
                bg: navItemBg.hover,
                borderWidth: "hairline",
                borderColor: "base.border",
                boxShadow: "buttonShadowLight",
              })}
        />
        {/* Beside the fill, not in it, so the fill's border never shifts it off the rail. */}
        {hasRailTick && (
          <Box
            position="absolute"
            left="17px"
            top={2}
            bottom={2}
            width="2px"
            bg="text.disabled"
          />
        )}
      </Box>
    </MotionBox>
  );
};

const noopSubscribe = () => () => {};
